import { useState, useEffect } from "react";
import { useAuth } from "@/context/use-auth";
import { Heading } from "@/components/tailwind/heading";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/tailwind/button";
import { LedgerFilters } from "@/components/ledger/ledger-filters";
import { LedgerSummary } from "@/components/ledger/ledger-summary";
import { LedgerTable } from "@/components/ledger/ledger-table";
import { LedgerEntryType, LedgerSummary as LedgerSummaryType } from "@/models/ledger";
import { getLedgerEntries, getEarliestLedgerYear, cancelLedgerEntry, downloadLedgerReceipt } from "@/services/ledger-services";
import { useToast } from "@/hooks/use-toast";
import { ChevronLeft, ChevronRight, BookOpen, Plus, Printer } from "lucide-react";
import { Text } from "@/components/tailwind/text";
import { Switch } from "@/components/tailwind/switch";
import { Field, Label } from "@/components/tailwind/fieldset";
import { LedgerEntryDialog } from "@/components/ledger/ledger-entry-dialog";
import { useLedgerTranslation } from "@/i18n/hooks";
import ConfirmationDialog, { useConfirmationDialog } from "@/components/confirmation-dialog";
import type { LedgerEntry } from "@/models/ledger";
import { formatDateString } from "@/lib/utils";
import log from '@/lib/logger'

function triggerBlobDownload(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

export default function LedgerPage() {
    const { token } = useAuth();
    const { toast } = useToast();
    const { t } = useLedgerTranslation();
    const confirmationDialog = useConfirmationDialog();
    
    // Filter states
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [type, setType] = useState<LedgerEntryType | 'ALL'>('ALL');
    const [search, setSearch] = useState('');
    const [searchAll, setSearchAll] = useState(false);
    
    // Pagination states
    const [page, setPage] = useState(0);
    
    // Data states
    const [summary, setSummary] = useState<LedgerSummaryType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
    const [earliestYear, setEarliestYear] = useState<number | null>(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [showCancelled, setShowCancelled] = useState(false);
    
    useEffect(() => {
        if (!token) return;

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const data = await getLedgerEntries(token, {
                    year,
                    month,
                    type: type === 'ALL' ? undefined : type,
                    page,
                    size: 20,
                    sort: 'voucherNumber,asc',
                    includeCancelled: showCancelled
                });

                if (data) {
                    setSummary(data);
                }
            } catch (error) {
                log.error('Error loading ledger data:', error);
                toast({
                    title: t('messages.error.title'),
                    description: t('messages.error.loadFailed'),
                    variant: 'destructive'
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [token, year, month, type, page, refreshTrigger, showCancelled]);
    
    // Load earliest year when component mounts
    useEffect(() => {
        const loadEarliestYear = async () => {
            if (!token) return;
            const year = await getEarliestLedgerYear(token);
            setEarliestYear(year);
        };
        loadEarliestYear();
    }, [token]);
    
    
    const handlePageChange = (newPage: number) => {
        if (newPage >= 0 && summary && newPage < summary.totalPages) {
            setPage(newPage);
        }
    };
    
    // Reset page when filters change (except search which is now client-side)
    useEffect(() => {
        setPage(0);
    }, [year, month, type]);
    
    const handleDialogSuccess = () => {
        setIsDialogOpen(false);
        setEditingEntryId(null);
        // Trigger reload by incrementing the refresh trigger
        setRefreshTrigger(prev => prev + 1);
    };

    const handleCreateClick = () => {
        setDialogMode('create');
        setEditingEntryId(null);
        setIsDialogOpen(true);
    };

    const handleEditEntry = (entry: LedgerEntry) => {
        setDialogMode('edit');
        setEditingEntryId(entry.id);
        setIsDialogOpen(true);
    };
    
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);
    };

    const handleCancelEntry = (entry: LedgerEntry) => {
        if (!token) return;
        
        const formattedDate = formatDateString(entry.transactionDate);
        const formattedAmount = formatCurrency(entry.amount);
        
        confirmationDialog.openWithConfig({
            title: `Buchung #${entry.voucherNumber} vom ${formattedDate} stornieren?`,
            description: `Soll die Buchung über ${formattedAmount} mit dem Buchungstext "${entry.description}" wirklich storniert werden? Diese Aktion kann nicht rückgängig gemacht werden.`,
            confirmButton: {
                text: 'Stornieren',
                ariaLabel: 'Eintrag stornieren',
                color: 'red'
            },
            cancelButton: {
                text: 'Abbrechen',
                ariaLabel: 'Abbrechen'
            }
        }, entry.id);
    };
    
    const handlePrintReceipt = async (entry: LedgerEntry) => {
        if (!token) return;

        try {
            const blob = await downloadLedgerReceipt(token, entry.id);
            triggerBlobDownload(blob, `eigenbeleg_${entry.voucherNumber}.pdf`);
        } catch (error) {
            log.error('Error downloading receipt:', error);
            toast({
                title: t('messages.error.title'),
                description: t('messages.error.receiptFailed'),
                variant: 'destructive'
            });
        }
    };

    const handlePrintPdf = async () => {
        if (!token) return;
        
        try {
            const apiUrl = import.meta.env.VITE_NBH_URL_ROOT || import.meta.env.VITE_API_URL || 'http://localhost:8080';
            const response = await fetch(
                `${apiUrl}/api/ledger/pdf?year=${year}&month=${month}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (!response.ok) {
                throw new Error('PDF download failed');
            }
            
            const blob = await response.blob();
            const monthPadded = month.toString().padStart(2, '0');
            triggerBlobDownload(blob, `kassenbuch_${monthPadded}_${year}.pdf`);
        } catch (error) {
            log.error('Error downloading PDF:', error);
            toast({
                title: t('messages.error.title'),
                description: 'Das Kassenbuch konnte nicht heruntergeladen werden.',
                variant: 'destructive'
            });
        }
    };
    
    return (
        <div className="container mx-auto py-6 max-w-7xl space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <BookOpen className="h-8 w-8 text-blue-500" />
                    <Heading>{t('page.title')}</Heading>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        plain
                        onClick={handlePrintPdf}
                        className="flex items-center gap-2"
                        title="Kassenbuch drucken"
                    >
                        <Printer className="h-4 w-4" />
                        <span className="hidden sm:inline">Drucken</span>
                    </Button>
                    <Button
                        onClick={handleCreateClick}
                        className="flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        {t('actions.addEntry')}
                    </Button>
                </div>
            </div>
            
            <Card className="p-6">
                <LedgerFilters
                    year={year}
                    month={month}
                    type={type}
                    search={search}
                    searchAll={searchAll}
                    earliestYear={earliestYear}
                    onYearChange={setYear}
                    onMonthChange={setMonth}
                    onTypeChange={setType}
                    onSearchChange={setSearch}
                    onSearchAllChange={setSearchAll}
                />
            </Card>
            
            {isLoading ? (
                <div className="flex justify-center items-center h-64">
                    <Text className="text-gray-500">{t('page.loading')}</Text>
                </div>
            ) : summary ? (
                <>
                    <LedgerSummary
                        startingBalance={summary.startingBalance}
                        endingBalance={summary.endingBalance}
                        monthlyTotal={summary.monthlyTotal}
                        totalIncome={summary.totalIncome}
                        totalExpenses={summary.totalExpenses}
                        orderIncome={summary.orderIncome}
                        openingBalanceEntry={summary.entries.find(e => e.type === 'OPENING_BALANCE')}
                    />
                    
                    <Card className="p-6">
                        <LedgerTable
                            entries={summary.entries}
                            searchTerm={search}
                            onCancelEntry={handleCancelEntry}
                            onEditEntry={handleEditEntry}
                            onPrintReceipt={handlePrintReceipt}
                        />
                        
                        <div className="mt-4 flex items-center justify-between border-t pt-4">
                            <Field className="flex items-center gap-2">
                                <Switch
                                    checked={showCancelled}
                                    onChange={setShowCancelled}
                                    color="blue"
                                />
                                <Label className="text-sm text-gray-600 dark:text-gray-400">
                                    Stornierte Einträge anzeigen
                                </Label>
                            </Field>
                        </div>
                        
                        {summary.totalPages > 1 && (
                            <div className="flex justify-between items-center mt-6">
                                <Text className="text-sm text-gray-500">
                                    {t('pagination.page', { current: summary.page + 1, total: summary.totalPages })} 
                                    ({t('pagination.entries', { count: summary.totalElements })})
                                </Text>
                                
                                <div className="flex gap-2">
                                    <Button
                                        plain
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page === 0}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                        {t('pagination.previous')}
                                    </Button>
                                    
                                    <Button
                                        plain
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page === summary.totalPages - 1}
                                    >
                                        {t('pagination.next')}
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </>
            ) : (
                <Card className="p-6">
                    <div className="flex justify-center items-center h-64">
                        <Text className="text-red-500">
                            {t('page.error')}
                        </Text>
                    </div>
                </Card>
            )}
            
            <LedgerEntryDialog
                isOpen={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                    setEditingEntryId(null);
                }}
                onSuccess={handleDialogSuccess}
                mode={dialogMode}
                entryId={editingEntryId}
            />
            
            {confirmationDialog.dialogProps && (
                <ConfirmationDialog
                    {...confirmationDialog.dialogProps}
                    onConfirm={async (entityId) => {
                        if (!entityId || !token) return;
                        try {
                            await cancelLedgerEntry(token, entityId);
                            toast({
                                title: 'Erfolgreich',
                                description: 'Der Eintrag wurde erfolgreich storniert.',
                            });
                            // Trigger reload
                            setRefreshTrigger(prev => prev + 1);
                            confirmationDialog.closeDialog();
                        } catch (error: any) {
                            log.error('Error cancelling entry:', error);
                            toast({
                                title: 'Fehler',
                                description: error?.detail || 'Der Eintrag konnte nicht storniert werden.',
                                variant: 'destructive'
                            });
                            confirmationDialog.closeDialog();
                        }
                    }}
                />
            )}
        </div>
    );
}
